// RecentActivity — виджет Dashboard: топ-N последних событий аудита (admin only) или
// recent items для обычных пользователей.
// U-25 (sled-V9): заголовки секций оформлены SectionHeader-ом (MD3): иконка + title слева,
// action («Все события» → /admin/audit-events) справа — вместо мелкого h6 сбоку.
import { useQuery } from '@tanstack/react-query';
import { Box, Card, CardContent, Stack, Typography, Alert, Button } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { Link } from 'react-router-dom';
import { SectionHeader } from './SectionHeader';
import { AuditEventsTimeline } from './AuditEventsTimeline';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { auditApi } from '@/api/audit';
import { t } from '@/i18n';

const RECENT_ITEMS_LIMIT = 10;

interface RecentActivityProps {
  /** Если true — загружаем audit-events (admin only). */
  showAuditEvents?: boolean;
}

/** Виджет последних событий/действий. */
export function RecentActivity({ showAuditEvents = false }: RecentActivityProps): JSX.Element {
  const auditQuery = useQuery({
    queryKey: ['audit-events', 'recent'],
    queryFn: () => auditApi.list({ page: 0, size: RECENT_ITEMS_LIMIT }),
    enabled: showAuditEvents,
  });

  if (!showAuditEvents) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={1.5}>
            <SectionHeader
              title={t('dashboard.recentActivityTitle')}
              icon={<HistoryIcon fontSize="small" color="action" />}
            />
            <Typography variant="body2" color="text.secondary">
              Журнал аудита доступен только администраторам. Используйте навигацию слева для
              перехода к задачам, сделкам или календарю.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const events = auditQuery.data?.content ?? [];

  return (
    <Card>
      <CardContent>
        <SectionHeader
          title={t('dashboard.auditLogTitle')}
          icon={<HistoryIcon fontSize="small" color="action" />}
          action={
            <Button size="small" component={Link} to="/admin/audit-events">
              {t('dashboard.auditLogAll')}
            </Button>
          }
        />
        {auditQuery.isLoading ? (
          <LoadingSpinner />
        ) : auditQuery.isError ? (
          <Alert severity="warning">
            {auditQuery.error instanceof Error ? auditQuery.error.message : 'Не удалось загрузить события'}
          </Alert>
        ) : events.length === 0 ? (
          <Box sx={{ color: 'text.secondary' }}>Нет событий</Box>
        ) : (
          <AuditEventsTimeline events={events} />
        )}
      </CardContent>
    </Card>
  );
}
